/**
 * Files in messages (Phase 5c): what the composer uploads. Big photos are made
 * smaller in the browser first (longest side 2560 pixels, WebP), so rooms stay
 * fast and agents can look at them; GIFs and documents go as they are.
 */
import { ApiError } from "./api";

export interface FileView {
  id: string;
  name: string;
  mime: string;
  size: number;
  width: number | null;
  height: number | null;
  kind: "image" | "pdf" | "text";
}

export const ACCEPT = "image/png,image/jpeg,image/webp,image/gif,application/pdf,text/plain,text/markdown,text/csv,.md,.markdown,.csv,.txt";
const MAX_SIDE = 2560;
const MAX_BYTES = 10 * 1024 * 1024;

export const sizeLabel = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

async function prepare(file: File): Promise<{ body: Blob; name: string; width?: number; height?: number }> {
  if (!/^image\/(png|jpeg|webp|gif)$/.test(file.type)) return { body: file, name: file.name };
  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) return { body: file, name: file.name };
  const { width, height } = bitmap;
  const scale = Math.min(1, MAX_SIDE / Math.max(width, height));
  if (file.type === "image/gif" || (scale === 1 && file.size <= 3 * 1024 * 1024)) {
    bitmap.close();
    return { body: file, name: file.name, width, height };
  }
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  const g = canvas.getContext("2d");
  if (!g) return { body: file, name: file.name, width, height };
  g.imageSmoothingQuality = "high";
  g.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, "image/webp", 0.9));
  if (!blob) return { body: file, name: file.name, width, height };
  return { body: blob, name: file.name.replace(/\.(png|jpe?g|webp)$/i, "") + ".webp", width: canvas.width, height: canvas.height };
}

export async function uploadFile(roomId: string, file: File): Promise<FileView> {
  const p = await prepare(file);
  if (p.body.size > MAX_BYTES) throw new ApiError("Files can be up to 10 MB.", 413);
  const res = await fetch(`/api/rooms/${roomId}/files`, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/octet-stream",
      "X-File-Name": encodeURIComponent(p.name || "file"),
      ...(p.width ? { "X-Image-Width": String(p.width), "X-Image-Height": String(p.height) } : {}),
    },
    body: p.body,
  });
  const data = (await res.json().catch(() => ({}))) as { file?: FileView; error?: string };
  if (!res.ok || !data.file) throw new ApiError(data.error ?? "Could not upload the file.", res.status);
  return data.file;
}

export async function removeUpload(id: string): Promise<void> {
  await fetch(`/api/files/${id}`, { method: "DELETE", credentials: "same-origin" }).catch(() => undefined);
}
